import { useLanguageStore } from '@/context/LanguageStore';
import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import consts from '@/config/consts';

type HomepageResponse = {
  data: Elvita.HomePageData;
  status: number;
  lastModified: string;
};

export function useFetchStartPage() {
  const { data: productConfig, isLoading, isError } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  const query = useQuery<HomepageResponse>({
    queryKey: ['startpage', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/startpage.json`,
        baseURL: consts.storageUrl,
      }).then(response => {
        return {
          data: response.data,
          status: response.status,
          lastModified: response.headers['last-modified'],
        };
      }),

    enabled: !!manifestId,
  });

  return {
    ...query,
    isError: isError || query.isError,
    isLoading: isLoading || query.isPending,
  };
}

export function useFetchSettings() {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['settings', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/settings.json`,
        baseURL: consts.storageUrl,
      }).then(response => response.data),

    enabled: !!manifestId,
  });
}

type BrowseResponse = { data: Elvita.Node[]; status: number };

export function useFetchBrowse(): UseQueryResult<BrowseResponse> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery<BrowseResponse>({
    queryKey: ['browse', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/browse.json`,
        baseURL: consts.storageUrl,
      }).then(response => {
        return { data: response.data, status: response.status };
      }),

    enabled: !!manifestId,
  });
}

type TopicResponse = { data: string; status: number };

export function useFetchTopic(topicId: string): UseQueryResult<TopicResponse> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['topic', manifestId, topicId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/html/${topicId}.html`,
        baseURL: consts.storageUrl,
      }).then(response => {
        return { data: response.data, status: response.status };
      }),

    enabled: !!manifestId && !!topicId,
  });
}

export function useFetchHotspots(): UseQueryResult<Elvita.Hotspot[]> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['hotspots', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/hotspots.json`,
        baseURL: consts.storageUrl,
      }).then(response => response.data),

    enabled: !!manifestId,
  });
}

type WizardStructureResponse = { data: Elvita.WizardItem[]; status: number };

export function useFetchWizardStructure(): UseQueryResult<WizardStructureResponse> {
  const lang = useLanguageStore(state => state.lang);

  return useQuery({
    queryKey: ['wizard', lang],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/configs/wizard_${lang}.json`,
        baseURL: consts.storageUrl,
      }).then(response => {
        const wizardData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        return {
          data: wizardData,
          status: response.status,
        };
      }),

    enabled: !!lang,
  });
}

export function useFetchMenu(): UseQueryResult<Elvita.MenuItem[]> {
  const { data: productConfig } = useProductConfig();
  const manifestId = productConfig?.ManifestId;

  return useQuery({
    queryKey: ['main-menu', manifestId],

    queryFn: () =>
      axios({
        method: 'get',
        url: `/manifests/${manifestId}/menu.json`,
        baseURL: consts.storageUrl,
      }).then(response => response.data),

    enabled: !!manifestId,
  });
}

const getProductConfigs = async () => {
  const response = await axios({
    method: 'get',
    url: `/configs/productconfigs.json`,
    baseURL: consts.storageUrl,
  });
  return response.data as Elvita.ProductConfigItem[];
};

export function useProductConfigs(): UseQueryResult<
  Elvita.ProductConfigItem[]
> {
  return useQuery({
    queryKey: ['product-configs'],

    queryFn: () => getProductConfigs(),
  });
}

export function useProductConfig(): UseQueryResult<Elvita.ProductConfigItem> {
  const { productId } = useParams<{ productId: string }>();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['product-configs', productId],

    queryFn: async () => {
      const res = await getProductConfigs();
      queryClient.setQueryData(['product-configs'], res);
      const productConfig = res.find(item => item.ProductId === productId);
      if (!productConfig) {
        throw new Error('Product not found');
      }
      return productConfig;
    },

    enabled: !!productId,

    initialData: () =>
      queryClient
        .getQueryData<Elvita.ProductConfigItem[]>(['product-configs'])
        ?.find(item => item.ProductId === productId),
  });
}
